import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Button from 'UIKit/Button'
import Dropdown from 'UIKit/Dropdown'
import { DropdownProps } from 'UIKit/Dropdown/types'
import Image from 'UIKit/Image'
import Tab from 'UIKit/Tab'
import Tabs from 'UIKit/Tabs'
import Text from 'UIKit/Text'
import Toggle from 'UIKit/Toggle'
import { NULL_FUNCTION } from 'config/constants'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import Column from 'layout/Components/Column'
import { ReactNode, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useAirdropBoxStorage, useRedeemAirdrop } from 'state/bonus/hooks'
import { useUserSettings } from 'state/profile/hooks'
import styled, { css } from 'styled-components'
import { colors } from 'theme/colors'
import { delayed } from 'utils'
import { SentryHelper } from 'utils/sentryHelper'
import AirdropBoxStorage from './AirdropBoxStorage'
import { SummaryAirdrop } from './SummaryAirdrop'
import { HUSDAirdropTab } from './airdrop'
import { useNotificationAirdropDesktop, useNotificationAirdropMobile } from './hooks'

const InstructionAirdropDropDown: React.FC<BoxProps & { onDismiss?: () => void }> = ({ onDismiss, ...props }) => {
  const { updateData, airdropBoxStorage } = useAirdropBoxStorage()
  const redeemAirdrop = useRedeemAirdrop()
  const { t } = useTranslation()
  const { isDesktop } = useMatchBreakpoints()
  const getNotification = useNotificationAirdropMobile()
  const { userSettings, updateSetting } = useUserSettings()
  const [tab, setTab] = useState(HUSDAirdropTab.Instruction)

  return (
    <InstructionContainer {...props}>
      <Text
        color="#000"
        textAlign="center"
        fontSize="20px"
        fontWeight={800}
        letterSpacing="-0.4px"
        mb="4px"
        textTransform="uppercase"
      >
        <Trans>HUSD Airdrop</Trans>
      </Text>
      <Text
        color="#232C3D"
        fontSize="12px"
        fontWeight={500}
        textAlign="center"
        letterSpacing="-0.24px"
        lineHeight="normal"
      >
        <Trans>
          When you bet, you open up the opportunity to receive an airdrop from HunnyPlay. The more you bet, the higher
          your chance of getting one.
        </Trans>
      </Text>

      <StyledContent my={12}>
        <StyledTabs
          value={tab}
          onTagChanged={setTab}
          backgroundBlurColor={colors.background}
          indicator={<StyledIndicator />}
          $active={tab}
        >
          <StyledTab value={HUSDAirdropTab.Instruction} $active={tab === HUSDAirdropTab.Instruction}>
            <Text bold small lineHeight="14px" color="#161C24" fontWeight={700}>
              <Trans>Instruction</Trans>
            </Text>
          </StyledTab>

          <StyledTabStroke disabled value={null}>
            <StyledStroke />
          </StyledTabStroke>

          <StyledTab value={HUSDAirdropTab.Summary} $active={tab === HUSDAirdropTab.Summary}>
            <Text bold small lineHeight="14px" color="#161C24" fontWeight={700}>
              <Trans>Summary</Trans>
            </Text>
          </StyledTab>
        </StyledTabs>
      </StyledContent>

      {tab === HUSDAirdropTab.Instruction && (
        <Box>
          <StyledFlex>
            <Flex minWidth={60} height={60} alignItems="center" justifyContent="center">
              <Image src="/images/airdrop/image-instruction.png" width={60} height={60} />
            </Flex>
            <StyledText>
              <Trans>Catch the gift when it dropping</Trans>
            </StyledText>
          </StyledFlex>
          <StyledFlex>
            <Flex minWidth={60} height={60} alignItems="center" justifyContent="center">
              <Image src="/images/airdrop/image-instruction1.png" width={60} height={60} />
            </Flex>
            <StyledText>
              <Trans>Or claim all the gifts we've helped you store in the bottom bar</Trans>
            </StyledText>
          </StyledFlex>
          <StyledFlex>
            <Flex minWidth={60} height={60} alignItems="center" justifyContent="center">
              <Image src="/images/airdrop/image-instruction2.png" width={60} height={60} />
            </Flex>
            <StyledText>
              <Trans>The gift may include HUSD, special bonuses, or nothing</Trans>
            </StyledText>
          </StyledFlex>
        </Box>
      )}

      {tab === HUSDAirdropTab.Summary && <SummaryAirdrop />}

      <Box>
        <StyledInlineBox my={12} />

        <Flex py={12} justifyContent="center">
          <Box mt="4px">
            <Toggle
              defaultColor="text"
              checkedColor="text"
              backgroundColor="modalHeaderBackground"
              checkedBackgroundColor="success"
              value={userSettings.isDisableAnimationAirdrop}
              onValueChanged={(value) => {
                updateSetting({
                  isDisableAnimationAirdrop: value,
                })
              }}
            />
          </Box>
          <Box ml="8px">
            <Text
              fontSize="12px"
              color="#232C3D"
              fontWeight={700}
              lineHeight="normal"
              letterSpacing="-0.28px"
              textAlign="start"
            >
              <Trans>Disable dropping animation</Trans>
            </Text>
            <Text
              fontSize="12px"
              color="#6C727E"
              fontWeight={500}
              lineHeight="normal"
              letterSpacing="-0.28px"
              textAlign="start"
            >
              <Trans>Airdrop will be automatically collected in the gift box.</Trans>
            </Text>
          </Box>
        </Flex>
      </Box>

      {!isDesktop && (
        <StyledButton
          fontSize={['14px', '', '12px']}
          primary
          mb="8px"
          width={['100%', '', 'max-content']}
          onClick={async () => {
            if (airdropBoxStorage.length === 0) {
              onDismiss()
              return
            }

            await redeemAirdrop(
              airdropBoxStorage,
              async () => {
                updateData([])
              },
              (boxes) => {
                getNotification(boxes)
              },
            )
            onDismiss()
          }}
        >
          <Text fontSize={['10px', '12px', '12px']} bold>
            {airdropBoxStorage.length === 0
              ? t('Got it')
              : // eslint-disable-next-line no-template-curly-in-string
                t('Claim {{value}} Airdrops', {
                  value: airdropBoxStorage.length,
                })}
          </Text>
        </StyledButton>
      )}
    </InstructionContainer>
  )
}

const StyledWrapper: React.FC<
  React.PropsWithChildren<Omit<DropdownProps, 'title'> & { title?: string | ReactNode }>
> = ({ children, title, dropdownContent, dropdownPosition, style, id, maxWidth, ...props }) => {
  return (
    <Box
      position="relative"
      maxWidth={maxWidth}
      display="flex"
      id={id}
      style={{
        cursor: 'pointer',
        ...style,
      }}
      className="wrapper"
    >
      <Dropdown
        display="flex"
        style={{
          flexDirection: 'column',
        }}
        dropdownContent={dropdownContent}
        dropdownPosition={dropdownPosition}
        {...props}
      >
        {title && (
          <StyledTitle
            minWidth="max-content"
            width="100%"
            fontSize={['12px !important', '', '10px !important']}
            mb="4px"
          >
            {title}
          </StyledTitle>
        )}
        <Flex alignItems="center" justifyContent="center" className="wrapper-container">
          {children}
        </Flex>
      </Dropdown>
    </Box>
  )
}

const AirdropBoxStorageDesktop: React.FC<BoxProps> = ({ ...props }) => {
  const { updateData, airdropBoxStorage } = useAirdropBoxStorage()
  const { isTablet } = useMatchBreakpoints()
  const redeemAirdrop = useRedeemAirdrop()
  const pushNotification = useNotificationAirdropDesktop()
  const redeemBoxRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(true)

  const handleAnimationRedeemed = async () => {
    setOpen(false)
    const targetMoveToElement = document.getElementById('airdrop-husd-logo')
    if (!targetMoveToElement || !redeemBoxRef.current) {
      SentryHelper.captureFeatureClientError({
        feature: '',
        error: new Error('Cannot init Element'),
        options: {
          payload: { targetMoveToElement, redeemBoxRef: redeemBoxRef.current },
        },
      })
    }

    const redeemBoxRect = redeemBoxRef.current.getBoundingClientRect()
    const targetMoveToElementRect = targetMoveToElement.getBoundingClientRect()
    redeemBoxRef.current.style.opacity = '1'

    redeemBoxRef.current.style.transform = `translate(${targetMoveToElementRect.left - redeemBoxRect.left}px, ${
      targetMoveToElementRect.top - redeemBoxRect.top
    }px) scale(0.625)`

    await delayed(1100)
    if (redeemBoxRef.current) {
      redeemBoxRef.current.style.opacity = '0'
      redeemBoxRef.current.style.transform = 'unset'
    }
    setOpen(true)
  }

  return (
    <StyledWrapper dropdownContent={open && <InstructionAirdropDropDown />} dropdownPosition="top-right" {...props}>
      <Box position="relative" className="warpper-box">
        <RedeemBox position="absolute" ref={redeemBoxRef} top="-2px" left="0px" height="32px" width="32px">
          <Image width={32} height={32} src="/images/airdrop/airdrop-gift-box.png" />
        </RedeemBox>

        <AirdropBoxStorage
          mr={['-8px', '', '20px']}
          position="relative"
          bottom={['9px', '', '2px']}
          left={['-1px', '', '0px']}
          display="flex"
          onClick={
            isTablet
              ? NULL_FUNCTION
              : async () => {
                  await redeemAirdrop(
                    airdropBoxStorage,
                    async () => {
                      updateData([])
                      await handleAnimationRedeemed()
                    },
                    (boxes) => {
                      pushNotification(boxes)
                    },
                  )
                  return
                }
          }
        />
      </Box>
    </StyledWrapper>
  )
}

export const RedeemBox = styled(Box)`
  opacity: 0;
  z-index: 5;
  pointer-events: none;
  transition: transform 1s;
  transform-origin: left top;
`

const InstructionContainer = styled(Box)`
  border-radius: 8px;
  position: relative;
  width: 300px;
  min-height: fit-content;
  padding: 20px 20px 8px 20px;
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;

  &:before {
    content: '';
    position: absolute;
    bottom: -1%;
    right: 10%;
    width: 10px;
    height: 10px;
    transform: rotate(135deg);
    border-radius: 1px;
    background: #fff;
    z-index: -2;
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;

    width: 100%;
    height: 100%;
    border-radius: 8px;
    background: radial-gradient(150.52% 137.62% at 0% 0%, #fff 0%, #fee9ed 100%);
    background-size: 100%;
    background-repeat: no-repeat;
    background-position: top;
  }
`

const StyledFlex = styled(Flex)`
  align-items: center;
`

const StyledText = styled(Text)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 8px;
  color: #1d2533;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.24px;
  line-height: normal;
`

const StyledInlineBox = styled(Box)`
  width: 100%;
  border-top: 1px solid #e0e0e0;
`

const StyledContent = styled(Column)`
  overflow: hidden;
  transition: ${({ theme }) => theme.transitions.medium};
`

const StyledIndicator = styled.div`
  border-radius: ${({ theme }) => theme.radii.tiny};
  background: rgba(255, 255, 255, 0.08);

  height: 100%;
  width: 100%;
`

const StyledTabs = styled(Tabs)<{ $active: number }>`
  padding: 4px;
  background: rgba(22, 28, 36, 0.04);
  border-radius: ${({ theme }) => theme.radii.tiny};

  box-shadow: 0px 2px 4px 4px rgba(0, 0, 0, 0.05);

  width: 100%;
  max-width: 100%;
`

const StyledTabStroke = styled(Tab)`
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  width: 16px;
  cursor: auto;
`

const StyledStroke = styled(Box)`
  width: 1px;
  height: 16px;
  background: ${({ theme }) => theme.colors.textTertiary};
`

const StyledTab = styled(Tab)<{ $active: boolean }>`
  padding: 8px 12px;
  min-width: 40px;
  background: ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.6)' : 'unset')};
  border-radius: 4px;
  width: 50%;
  line-height: 16px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  svg {
    min-width: 20px;
    transition: ${({ theme }) => theme.transitions.fast};
    fill: ${({ theme, $active }) => ($active ? `${theme.colors.textTertiary} !important` : theme.colors.textSubtle)};
  }

  ${Text} {
    transition: ${({ theme }) => theme.transitions.fast};
    color: ${({ theme, $active }) => ($active ? '#161C24 !important' : theme.colors.textSubtle)};
  }
`

export const StyledButton = styled(Button)<{ fontSize?: any; primary?: boolean }>`
  width: 100%;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  padding: 8px 12px;
  font-size: ${({ fontSize }) => fontSize || '10px'};
  height: ${({ height }) => height || '40px'};
  font-weight: 600;

  white-space: nowrap;
  text-align: center;

  &:hover {
    transform: scale(1) !important;
  }

  ${({ primary, theme }) =>
    primary &&
    css`
      background: ${theme.colors.primaryAlt};
      border-color: ${theme.colors.primaryAlt};
    `}

  &:disabled {
    background: rgba(255, 255, 255, 0.08);

    ${({ primary, theme }) =>
      primary &&
      css`
        background: ${theme.colors.primaryDisabled};
      `}
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    height: max-content;
  }
`

export const StyledTitle = styled(Text)`
  display: flex;
  align-items: center;

  color: ${({ theme }) => theme.colors.textAlt1};
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.24px;
`

export default AirdropBoxStorageDesktop
